import { useCallback, useEffect, useState } from "react";
import { apiRequest, type LinkedAccount } from "../lib/api";

interface LinkedAccountsProps {
  token: string | null;
  providerUsernames: { lichess: string | null; chesscom: string | null };
  onLocalUsername: (provider: "lichess" | "chesscom", username: string | null) => void;
}

const PROVIDERS: Array<{ id: "lichess" | "chesscom"; label: string; hint: string }> = [
  { id: "lichess", label: "Lichess", hint: "Your Lichess username" },
  { id: "chesscom", label: "Chess.com", hint: "Your Chess.com username" },
];

export function LinkedAccounts({ token, providerUsernames, onLocalUsername }: LinkedAccountsProps) {
  const [accounts, setAccounts] = useState<LinkedAccount[]>([]);
  const [usernames, setUsernames] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!token) {
      setAccounts([]);
      return;
    }
    const result = await apiRequest<{ accounts: LinkedAccount[] }>("/api/accounts", { token });
    if (result.data?.accounts) setAccounts(result.data.accounts);
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const link = async (provider: "lichess" | "chesscom") => {
    const username = (usernames[provider] ?? "").trim();
    if (!username) return;
    setBusy(provider);
    setMessage(null);
    const result = await apiRequest<{ linked: boolean }>("/api/accounts/link", {
      token,
      method: "POST",
      body: { provider, username },
    });
    setBusy(null);
    if (result.error) {
      setMessage(result.error);
      return;
    }
    onLocalUsername(provider, username);
    setMessage(`${provider === "lichess" ? "Lichess" : "Chess.com"} linked. The next sync runs within the hour.`);
    await refresh();
  };

  const unlink = async (provider: "lichess" | "chesscom") => {
    setBusy(provider);
    setMessage(null);
    const result = await apiRequest<{ unlinked: boolean }>(`/api/accounts/link/${provider}`, {
      token,
      method: "DELETE",
    });
    setBusy(null);
    if (result.error) {
      setMessage(result.error);
      return;
    }
    onLocalUsername(provider, null);
    setMessage(`${provider === "lichess" ? "Lichess" : "Chess.com"} unlinked.`);
    await refresh();
  };

  return (
    <section className="linked">
      <h3 className="linked__title">Linked accounts</h3>
      <p className="planned__desc">
        Link a username and the server pulls your recent games once an hour. The games land in your
        account, and you can analyse them here like any other game.
      </p>

      <ul className="linked__list">
        {PROVIDERS.map((provider) => {
          const linked = accounts.find((account) => account.provider === provider.id);
          const value = usernames[provider.id] ?? providerUsernames[provider.id] ?? "";
          return (
            <li className="linked__row" key={provider.id}>
              <div className="linked__fields">
                <label className="field__label" htmlFor={`linked-${provider.id}`}>
                  {provider.label}
                </label>
                <input
                  id={`linked-${provider.id}`}
                  className="input"
                  placeholder={provider.hint}
                  value={value}
                  onChange={(event) =>
                    setUsernames((current) => ({ ...current, [provider.id]: event.target.value }))
                  }
                />
              </div>
              {linked?.username ? (
                <div className="linked__state">
                  <span className="chip chip--live">
                    <span className="chip__dot" />
                    {linked.username}
                  </span>
                  <span className="linked__sync">
                    {linked.last_synced_at
                      ? `synced ${new Date(linked.last_synced_at).toLocaleString()}`
                      : "waiting for the first sync"}
                  </span>
                </div>
              ) : null}
              <div className="linked__actions">
                {linked?.username ? (
                  <button
                    className="btn btn--ghost"
                    onClick={() => void unlink(provider.id)}
                    disabled={busy === provider.id}
                  >
                    Unlink
                  </button>
                ) : (
                  <button
                    className="btn btn--primary"
                    onClick={() => void link(provider.id)}
                    disabled={busy === provider.id || !value.trim()}
                  >
                    {busy === provider.id ? "Linking…" : "Link"}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {message ? <p className="notice">{message}</p> : null}
    </section>
  );
}
